import clsx from 'clsx';
import Downshift, { StateChangeOptions } from 'downshift';
import { ReactNode, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Spinner } from '~components/generic/spinner/Spinner';
import useLabels from '~hooks/useLabels';
import { useSearchUser } from '~services/api/hooks/user-management.hooks';
import { hasOwnProperty } from '~utils/object';
import styles from './UserSearchAutocomplete.module.css';

const itemToValue = (item: UserSearchResultItem) => (item ? item.username : '');

export type UserSearchAutocompleteProps = {
  onSelect: (username: string, id: number) => void;
  disabled?: boolean;
  icon?: ReactNode;
  label?: string;
  placeholderText?: string;
};

export const UserSearchAutocomplete = ({
  onSelect,
  disabled = false,
  icon,
  label,
  placeholderText = 'placeholderSearchUser',
}: UserSearchAutocompleteProps) => {
  const { labelSearchUserUsername, labelSearchUserMail } = useLabels([
    'labelSearchUserMail',
    'labelSearchUserUsername',
  ]);
  const [searchInput, setSearchInput] = useState('');

  // Define: find user query
  const { data: userSuggestions, isFetching: isSearching } = useSearchUser(searchInput, { initialData: [] });

  // Handler: state changes of downshift component (input onChange or dropdown select)
  const handleStateChange = useCallback(
    (changes: StateChangeOptions<UserSearchResultItem>) => {
      if (!disabled) {
        if (hasOwnProperty(changes, 'selectedItem') && changes.selectedItem) {
          onSelect(changes.selectedItem.username, changes.selectedItem.uid);
          setSearchInput('');
        } else if (hasOwnProperty(changes, 'inputValue')) {
          const newSearchInput = (changes.inputValue || '').toLowerCase().trim();
          if (newSearchInput !== searchInput) {
            setSearchInput(newSearchInput);
          }
        }
      }
    },
    [onSelect, searchInput, disabled],
  );

  return (
    <Downshift itemToString={itemToValue} onStateChange={handleStateChange}>
      {({ getInputProps, getItemProps, highlightedIndex, isOpen, getLabelProps, getMenuProps }) => (
        <div className={clsx(styles.container, disabled && styles.containerDisabled)}>
          <label {...getLabelProps({ className: styles.label })}>
            {Boolean(icon) && <span className={styles.labelIcon}>{icon}</span>}
            {Boolean(label) && (
              <span className={styles.labelText}>
                <FormattedMessage id={label} defaultMessage={label} />
              </span>
            )}
          </label>
          <div className={styles.positioner}>
            <div className={styles.inputWrapper}>
              <FormattedMessage id={placeholderText} defaultMessage={placeholderText}>
                {placeholder => (
                  <input
                    {...getInputProps({
                      placeholder: String(placeholder),
                      value: searchInput,
                      className: styles.input,
                      disabled,
                    })}
                  />
                )}
              </FormattedMessage>
            </div>
            {isSearching && (
              <div className={styles.processingIndicator}>
                <Spinner />
              </div>
            )}
            <div className={styles.menuWrapper}>
              {isOpen && (
                <div
                  {...getMenuProps({ className: clsx(styles.menu, userSuggestions.length > 0 && styles.menuVisible) })}
                >
                  {!!userSuggestions &&
                    userSuggestions.slice(0, 10).map((item, index) => (
                      <div
                        key={item.uid}
                        {...getItemProps({
                          key: index,
                          index,
                          item,
                          className: clsx(styles.item, highlightedIndex === index && styles.itemSelected),
                        })}
                      >
                        <div className={styles.itemName}>{item.fullname}</div>
                        <div className={styles.itemDetails}>
                          <div className={styles.itemUser}>
                            {labelSearchUserUsername}: {item.username}
                          </div>
                          <div className={styles.itemMail}>
                            {labelSearchUserMail}: {item.mail}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Downshift>
  );
};
